import { Configuration, PublicClientApplication, LogLevel } from '@azure/msal-browser';

// This module is evaluated the moment anything imports the API service, and that
// chain is reachable from plain utility modules (a column builder that renders a
// button, say). Those get imported by node-environment unit tests, where there is
// no `window` — and a bare `window.location` here threw at import time, failing
// tests that never touch auth. The values only matter in a browser, so fall back
// to inert defaults instead of exploding.
const origin = typeof window !== 'undefined' ? window.location.origin : '';
const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';

export const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_AD_CLIENT_ID || '',
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_AD_TENANT_ID}`,
    redirectUri: import.meta.env.VITE_REDIRECT_URI || origin,
    postLogoutRedirectUri: import.meta.env.VITE_REDIRECT_URI || origin,
    navigateToLoginRequestUrl: false,
  },
  cache: {
    // Use localStorage to share auth state across tabs
    // This allows opening links in new tabs without re-login
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: true,
    secureCookies: isHttps,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            return;
          case LogLevel.Info:
            return;
          case LogLevel.Verbose:
            return;
          case LogLevel.Warning:
            return;
        }
      },
      logLevel: LogLevel.Warning, // Reduce log verbosity
    },
    iframeHashTimeout: 10000, // Increase iframe timeout to 10 seconds
    loadFrameTimeout: 10000, // Increase frame load timeout
    windowHashTimeout: 10000, // Increase hash timeout
  },
};

// Scopes for API access - request ReadWrite by default
// Azure AD will grant based on user's permissions
export const apiScopes = {
  read: [`api://${import.meta.env.VITE_AZURE_AD_API_CLIENT_ID}/Data.Read`],
  readWrite: [`api://${import.meta.env.VITE_AZURE_AD_API_CLIENT_ID}/Data.ReadWrite`],
};

export const loginRequest = {
  scopes: [
    'openid',
    'profile',
    'email',
    'User.Read',
    'Group.Read.All',
    // Request API access scope
    `api://${import.meta.env.VITE_AZURE_AD_API_CLIENT_ID}/Data.ReadWrite`,
  ],
  prompt: 'select_account',
};

// Token request for API calls (used by api.ts interceptor)
export const apiTokenRequest = {
  scopes: [`api://${import.meta.env.VITE_AZURE_AD_API_CLIENT_ID}/Data.ReadWrite`],
};

// Create a singleton instance of MSAL - use window object to ensure true singleton
declare global {
  interface Window {
    msalSingleton?: PublicClientApplication;
  }
}

export const getMsalInstance = (): PublicClientApplication => {
  if (!window.msalSingleton) {
    window.msalSingleton = new PublicClientApplication(msalConfig);

    // Initialize MSAL only once
    window.msalSingleton.initialize().then(() => {
      // Handle redirect promise to process hash fragments properly
      window.msalSingleton!.handleRedirectPromise().then((response) => {
        if (response) {
          window.msalSingleton!.setActiveAccount(response.account);
        } else {
          // Account selection logic
          const accounts = window.msalSingleton!.getAllAccounts();
          if (accounts.length > 0) {
            window.msalSingleton!.setActiveAccount(accounts[0]);
          }
        }
      }).catch((error) => {
        // Clear any problematic hash fragments
        if (window.location.hash && window.location.hash.includes('error')) {
          window.location.hash = '';
        }
      });

      window.msalSingleton!.addEventCallback((event) => {
        if (event.eventType === 'msal:loginSuccess' && event.payload) {
          const account = (event.payload as any).account;
          window.msalSingleton!.setActiveAccount(account);
        }
      });
    }).catch((error) => {
    });
  }

  return window.msalSingleton;
};

export const msalInstance = getMsalInstance();

export const isAzureADConfigured = (): boolean => {
  return !!import.meta.env.VITE_AZURE_AD_CLIENT_ID &&
         !!import.meta.env.VITE_AZURE_AD_TENANT_ID;
};
